/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import './AdminSecurity.css';

// ═══════════════════════════════════════════════════════════════════════════
// PASSWORD STRENGTH CALCULATOR (matches PasswordPolicyService)
// ═══════════════════════════════════════════════════════════════════════════
const calculatePasswordStrength = (password, context = {}) => {
  if (!password) {
    return { score: 0, label: '', color: '#e5e7eb', feedback: [] };
  }

  let score = 0;
  const feedback = [];

  // Length scoring (12 minimum for admin)
  if (password.length >= 16) {
    score += 30;
  } else if (password.length >= 14) {
    score += 25;
  } else if (password.length >= 12) {
    score += 20;
  } else if (password.length >= 8) {
    score += 10;
    feedback.push('Use at least 12 characters');
  } else {
    feedback.push('Password too short (minimum 12 characters)');
  }

  // Character variety
  if (/[a-z]/.test(password)) score += 10;
  else feedback.push('Add lowercase letters');

  if (/[A-Z]/.test(password)) score += 15;
  else feedback.push('Add uppercase letters');

  if (/[0-9]/.test(password)) score += 15;
  else feedback.push('Add numbers');

  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)) score += 20;
  else feedback.push('Add special characters (!@#$%^&*...)');

  // Bonus for mixing
  const varietyCount = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/]
    .filter(r => r.test(password)).length;
  if (varietyCount === 4) score += 10;

  // Penalties
  if (/(.)\1{2,}/.test(password)) {
    score -= 10;
    feedback.push('Avoid repeated characters');
  }

  if (/^[a-zA-Z]+$/.test(password) || /^[0-9]+$/.test(password)) {
    score -= 10;
    feedback.push('Mix letters, numbers, and symbols');
  }

  // Context checks
  if (context.username && password.toLowerCase().includes(context.username.toLowerCase())) {
    score -= 20;
    feedback.push("Don't include your username");
  }

  if (context.email) {
    const emailLocal = context.email.split('@')[0].toLowerCase();
    if (password.toLowerCase().includes(emailLocal)) {
      score -= 20;
      feedback.push("Don't include your email");
    }
  }

  // Common patterns
  const commonPatterns = ['123456', 'password', 'qwerty', 'admin', 'letmein'];
  if (commonPatterns.some(p => password.toLowerCase().includes(p))) {
    score -= 30;
    feedback.push('Avoid common password patterns');
  }

  // Clamp score
  score = Math.max(0, Math.min(100, score));

  // Determine label and color
  let label, color;
  if (score >= 80) {
    label = 'Strong';
    color = '#16a34a';
  } else if (score >= 60) {
    label = 'Good';
    color = '#2563eb';
  } else if (score >= 40) {
    label = 'Fair';
    color = '#d97706';
  } else if (score >= 20) {
    label = 'Weak';
    color = '#ea580c';
  } else {
    label = 'Very Weak';
    color = '#dc2626';
  }

  return { score, label, color, feedback };
};

// ═══════════════════════════════════════════════════════════════════════════
// PASSWORD STRENGTH METER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const PasswordStrengthMeter = ({ password, context = {} }) => {
  const { score, label, color, feedback } = calculatePasswordStrength(password, context);

  if (!password) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      {/* Progress bar */}
      <div style={{
        height: '6px',
        backgroundColor: '#e5e7eb',
        borderRadius: '3px',
        overflow: 'hidden',
        marginBottom: '6px'
      }}>
        <div style={{
          height: '100%',
          width: `${score}%`,
          backgroundColor: color,
          transition: 'width 0.3s, background-color 0.3s'
        }} />
      </div>

      {/* Label */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '12px'
      }}>
        <span style={{ color, fontWeight: 600 }}>{label}</span>
        <span style={{ color: '#6b7280' }}>{score}%</span>
      </div>

      {/* Feedback */}
      {feedback.length > 0 && (
        <ul style={{
          margin: '8px 0 0 0',
          padding: '0 0 0 16px',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          {feedback.slice(0, 3).map((tip, i) => (
            <li key={i} style={{ marginBottom: '2px' }}>{tip}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const AdminSecurity = () => {
  const navigate = useNavigate();
  const { user, logout } = useAdminAuth();

  // 2FA State
  const [twoFAStatus, setTwoFAStatus] = useState({
    enabled: false,
    backupCodesRemaining: 0,
    enabledAt: null,
    loading: true
  });
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // Password State
  const [passwordMode, setPasswordMode] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // General State
  const [actionPassword, setActionPassword] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch 2FA status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await adminApi.get2FAStatus();
      if (response.success) {
        setTwoFAStatus({
          enabled: response.data.enabled,
          backupCodesRemaining: response.data.backupCodesRemaining,
          enabledAt: response.data.enabledAt,
          loading: false
        });
      }
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
      setTwoFAStatus(prev => ({ ...prev, loading: false }));
    }
  };

  // ========== 2FA Setup ==========
  const initiateSetup = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await adminApi.setup2FA();
      if (response.success) {
        setQrCode(response.data.qrCode);
        setManualKey(response.data.manualEntryKey);
        setSetupMode(true);
      } else {
        setError(response.message || 'Failed to start 2FA setup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await adminApi.enable2FA(verificationCode);
      if (response.success) {
        setBackupCodes(response.data.backupCodes);
        setShowBackupCodes(true);
        setSetupMode(false);
        setVerificationCode('');
        setSuccess('2FA enabled successfully! Save your backup codes.');
        fetchStatus();
      } else {
        setError(response.message || 'Invalid code');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const cancelSetup = () => {
    setSetupMode(false);
    setQrCode('');
    setManualKey('');
    setVerificationCode('');
    setError('');
  };

  // ========== Disable 2FA ==========
  const disable2FA = async () => {
    if (!actionPassword) {
      setError('Password is required');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await adminApi.disable2FA(actionPassword);
      if (response.success) {
        setShowDisableConfirm(false);
        setActionPassword('');
        setSuccess('2FA has been disabled. Your account is now less secure.');
        fetchStatus();
      } else {
        setError(response.message || 'Failed to disable 2FA');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  // ========== Regenerate Backup Codes ==========
  const regenerateBackupCodes = async () => {
    if (!actionPassword) {
      setError('Password is required');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await adminApi.regenerateBackupCodes(actionPassword);
      if (response.success) {
        setBackupCodes(response.data.backupCodes);
        setShowBackupCodes(true);
        setShowRegenerateConfirm(false);
        setActionPassword('');
        setSuccess('New backup codes generated. Old codes are no longer valid.');
        fetchStatus();
      } else {
        setError(response.message || 'Failed to regenerate codes');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  // ========== Change Password ==========
  const validatePasswordLocally = () => {
    const { current, new: newPass, confirm } = passwords;
    
    if (!current || !newPass || !confirm) {
      return 'All password fields are required';
    }

    if (newPass !== confirm) {
      return 'New passwords do not match';
    }

    // FIXED: 12 character minimum for admin
    if (newPass.length < 12) {
      return 'New password must be at least 12 characters';
    }

    // Check complexity
    if (!/[a-z]/.test(newPass)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[A-Z]/.test(newPass)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[0-9]/.test(newPass)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPass)) {
      return 'Password must contain at least one special character';
    }

    return null;
  };

  const changePassword = async () => {
    const validationError = validatePasswordLocally();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await adminApi.changePassword(passwords.current, passwords.new);
      if (response.success) {
        setPasswordMode(false);
        setPasswords({ current: '', new: '', confirm: '' });
        setSuccess('Password changed successfully. Other sessions have been terminated.');
      } else {
        setError(response.message || 'Failed to change password');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // ========== Backup Codes Helpers ==========
  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setSuccess('Backup codes copied to clipboard!');
  };

  const downloadBackupCodes = () => {
    const codesText = `SkillDelivery Admin - 2FA Backup Codes
Generated: ${new Date().toISOString()}
User: ${user?.username}

IMPORTANT: Store these codes securely. Each code can only be used once.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

If you lose access to your authenticator app, use one of these codes to login.
`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skilldelivery-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ========== Render ==========
  return (
    <AdminLayout>
      <div className="admin-security-page">
        {/* Header */}
        <div className="security-header">
          <button className="back-button" onClick={() => navigate('/admin/dashboard')}>
            ← Back to Dashboard
          </button>
          <h1>🔐 Security Settings</h1>
          <p>Manage your account security and two-factor authentication</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <span>⚠️</span> {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}
        {success && (
          <div className="alert alert-success">
            <span>✅</span> {success}
            <button onClick={() => setSuccess('')}>×</button>
          </div>
        )}

        {/* ========== Backup Codes Modal ========== */}
        {showBackupCodes && backupCodes.length > 0 && (
          <div className="modal-overlay">
            <div className="modal backup-codes-modal">
              <h2>⚠️ Save Your Backup Codes</h2>
              <p className="warning-text">
                These codes will <strong>NOT</strong> be shown again. Store them in a secure location.
                Each code can only be used once.
              </p>

              <div className="codes-grid">
                {backupCodes.map((code, index) => (
                  <div key={index} className="code-item">
                    <span className="code-number">{index + 1}.</span>
                    <code>{code}</code>
                  </div>
                ))}
              </div>

              <div className="backup-actions">
                <button className="btn btn-secondary" onClick={copyBackupCodes}>
                  📋 Copy All
                </button>
                <button className="btn btn-secondary" onClick={downloadBackupCodes}>
                  💾 Download
                </button>
                <button className="btn btn-primary" onClick={() => setShowBackupCodes(false)}>
                  I've Saved These Codes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Two-Factor Authentication Section ========== */}
        <div className="security-section">
          <div className="section-header">
            <h2>🔑 Two-Factor Authentication (2FA)</h2>
            {twoFAStatus.loading ? (
              <span className="status-badge loading">Loading...</span>
            ) : twoFAStatus.enabled ? (
              <span className="status-badge enabled">✓ Enabled</span>
            ) : (
              <span className="status-badge disabled">✗ Disabled</span>
            )}
          </div>

          {/* 2FA Not Enabled */}
          {!twoFAStatus.enabled && !setupMode && (
            <div className="section-content">
              <p className="section-description">
                Add an extra layer of security to your admin account. When enabled, you'll need to 
                enter a code from your authenticator app each time you sign in.
              </p>

              <div className="info-box">
                <h4>📱 What you'll need:</h4>
                <ul>
                  <li>An authenticator app (Google Authenticator, Authy, Microsoft Authenticator)</li>
                  <li>Your phone or tablet to scan the QR code</li>
                </ul>
              </div>

              <button 
                className="btn btn-primary btn-large" 
                onClick={initiateSetup}
                disabled={loading}
              >
                {loading ? 'Setting up...' : '🔐 Enable Two-Factor Authentication'}
              </button>
            </div>
          )}

          {/* 2FA Setup Mode */}
          {setupMode && (
            <div className="section-content setup-mode">
              <div className="setup-step">
                <h3>Step 1: Scan QR Code</h3>
                <p>Open your authenticator app and scan this QR code:</p>
                
                <div className="qr-container">
                  <img src={qrCode} alt="2FA QR Code" className="qr-code" />
                </div>

                <div className="manual-entry">
                  <p>Can't scan? Enter this key manually:</p>
                  <code className="manual-key">{manualKey}</code>
                </div>
              </div>

              <div className="setup-step">
                <h3>Step 2: Verify Setup</h3>
                <p>Enter the 6-digit code from your authenticator app:</p>
                
                <div className="verify-input">
                  <input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="code-input"
                  />
                </div>

                <div className="setup-actions">
                  <button className="btn btn-secondary" onClick={cancelSetup}>
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={confirmSetup}
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Confirm & Enable'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 2FA Enabled */}
          {twoFAStatus.enabled && !setupMode && (
            <div className="section-content">
              <div className="status-info">
                <div className="info-item">
                  <span className="label">Status</span>
                  <span className="value enabled">✓ Active</span>
                </div>
                <div className="info-item">
                  <span className="label">Enabled On</span>
                  <span className="value">
                    {twoFAStatus.enabledAt 
                      ? new Date(twoFAStatus.enabledAt).toLocaleDateString() 
                      : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Backup Codes</span>
                  <span className={`value ${twoFAStatus.backupCodesRemaining <= 3 ? 'warning' : ''}`}>
                    {twoFAStatus.backupCodesRemaining} remaining
                  </span>
                </div>
              </div>

              {twoFAStatus.backupCodesRemaining <= 3 && (
                <div className="warning-box">
                  ⚠️ You're running low on backup codes. Consider generating new ones.
                </div>
              )}

              <div className="twofa-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowRegenerateConfirm(true)}
                >
                  🔄 Regenerate Backup Codes
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => setShowDisableConfirm(true)}
                >
                  🚫 Disable 2FA
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ========== Password Section ========== */}
        <div className="security-section">
          <div className="section-header">
            <h2>🔒 Password</h2>
          </div>

          {!passwordMode ? (
            <div className="section-content">
              <p className="section-description">
                Change your password regularly to keep your account secure. 
                Admin passwords must be at least 12 characters with uppercase, lowercase, numbers, and symbols.
              </p>
              
              {/* Password Policy Info */}
              <div className="info-box" style={{ marginBottom: '16px' }}>
                <h4>🔐 Password Requirements:</h4>
                <ul>
                  <li>Minimum 12 characters</li>
                  <li>At least one uppercase letter (A-Z)</li>
                  <li>At least one lowercase letter (a-z)</li>
                  <li>At least one number (0-9)</li>
                  <li>At least one special character (!@#$%^&*...)</li>
                  <li>Cannot reuse your last 5 passwords</li>
                  <li>Password expires every 60 days</li>
                </ul>
              </div>

              <button 
                className="btn btn-secondary"
                onClick={() => setPasswordMode(true)}
              >
                Change Password
              </button>
            </div>
          ) : (
            <div className="section-content">
              <div className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwords.current}
                      onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      {showPasswords.current ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>New Password (min. 12 characters)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwords.new}
                      onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      {showPasswords.new ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  
                  {/* Password Strength Meter */}
                  <PasswordStrengthMeter 
                    password={passwords.new} 
                    context={{ username: user?.username, email: user?.email }}
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  
                  {/* Match indicator */}
                  {passwords.confirm && (
                    <div style={{ 
                      marginTop: '6px', 
                      fontSize: '12px',
                      color: passwords.new === passwords.confirm ? '#16a34a' : '#dc2626'
                    }}>
                      {passwords.new === passwords.confirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setPasswordMode(false);
                      setPasswords({ current: '', new: '', confirm: '' });
                      setShowPasswords({ current: false, new: false, confirm: false });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={changePassword}
                    disabled={loading}
                  >
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ========== Recovery Options Section ========== */}
        <div className="security-section">
          <div className="section-header">
            <h2>🆘 Recovery Options</h2>
          </div>
          <div className="section-content">
            <div className="info-box recovery-info">
              <h4>If you lose access to your authenticator:</h4>
              <ol>
                <li><strong>Use a backup code</strong> - Enter one of your saved backup codes instead of the 6-digit code during login</li>
                <li><strong>Contact Super Admin</strong> - If you've used all backup codes, contact a super admin to manually disable 2FA on your account</li>
              </ol>
              <p className="note">
                💡 <strong>Tip:</strong> Store your backup codes in a password manager or print them and keep in a secure location.
              </p>
            </div>
          </div>
        </div>

        {/* ========== Session Info Section ========== */}
        <div className="security-section">
          <div className="section-header">
            <h2>📊 Current Session</h2>
          </div>
          <div className="section-content">
            <div className="session-info">
              <div className="info-item">
                <span className="label">Logged in as</span>
                <span className="value">{user?.username} ({user?.email})</span>
              </div>
              <div className="info-item">
                <span className="label">Role</span>
                <span className="value">{user?.role}</span>
              </div>
            </div>
            <button className="btn btn-danger" onClick={logout}>
              🚪 Logout
            </button>
          </div>
        </div>

        {/* ========== Disable Confirmation Modal ========== */}
        {showDisableConfirm && (
          <div className="modal-overlay">
            <div className="modal confirm-modal">
              <h2>⚠️ Disable Two-Factor Authentication?</h2>
              <p>This will make your account less secure. Are you sure?</p>
              
              <div className="form-group">
                <label>Enter your password to confirm:</label>
                <input
                  type="password"
                  value={actionPassword}
                  onChange={(e) => setActionPassword(e.target.value)}
                  placeholder="Your password"
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDisableConfirm(false);
                    setActionPassword('');
                    setError('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={disable2FA}
                  disabled={loading || !actionPassword}
                >
                  {loading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Regenerate Confirmation Modal ========== */}
        {showRegenerateConfirm && (
          <div className="modal-overlay">
            <div className="modal confirm-modal">
              <h2>🔄 Regenerate Backup Codes?</h2>
              <p>This will invalidate all your existing backup codes. New codes will be generated.</p>
              
              <div className="form-group">
                <label>Enter your password to confirm:</label>
                <input
                  type="password"
                  value={actionPassword}
                  onChange={(e) => setActionPassword(e.target.value)}
                  placeholder="Your password"
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRegenerateConfirm(false);
                    setActionPassword('');
                    setError('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={regenerateBackupCodes}
                  disabled={loading || !actionPassword}
                >
                  {loading ? 'Generating...' : 'Generate New Codes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSecurity;
